// Loads the serious, scientifically-sourced "30x gaze chain" from
// /gaze_factors.json and exposes small helpers to walk the chain (each factor
// links to the next, with factor 30 looping back to 1) and to look a factor up
// by its gaze event name.
//
// This is the scientific counterpart to the novelty animal layer: every factor
// cites a real peer-reviewed paper and/or a real open-source repo. The loader
// itself makes no claims; it just serves the vetted data file.

/** A citation to a real paper (arXiv / DOI / venue URL). */
export interface GazePaper {
  title: string;
  year: number;
  url: string;
}

/** A citation to a real open-source repository. */
export interface GazeRepo {
  /** "owner/name" as shown on the host. */
  name: string;
  url: string;
  /** Rough star count when known, else null. */
  stars: number | null;
}

/** One link in the gaze processing chain. */
export interface GazeFactor {
  id: number;
  /** The gaze event or phenomenon, e.g. "fixation", "microsaccades". */
  event: string;
  title: string;
  /** How a camera captures or derives this (one sentence). */
  capture: string;
  /** What it means or how it is interpreted (one sentence). */
  interpret: string;
  /** Algorithm or model name. */
  method: string;
  /** Canonical paper, or null when only a repo is cited. */
  paper: GazePaper | null;
  /** Best repo, or null when only a paper is cited. */
  repo: GazeRepo | null;
  /** One punchy "wow-factor" line a UI can display. */
  wow: string;
  /** id of the next factor in the chain (30 loops back to 1). */
  chain_to: number;
}

/** Shape of the /gaze_factors.json file. */
interface GazeChainFile {
  title: string;
  note: string;
  factors: GazeFactor[];
}

/** Cached factor list (empty until loadGazeChain resolves). */
let FACTORS: GazeFactor[] = [];
/** Guards against loading more than once (shared in-flight promise). */
let loadPromise: Promise<GazeFactor[]> | null = null;

/**
 * Type guard for a single factor from the JSON file. Keeps only well-formed
 * entries so a malformed file cannot break callers.
 */
function isFactor(v: unknown): v is GazeFactor {
  if (typeof v !== "object" || v === null) return false;
  const f = v as Record<string, unknown>;
  return (
    typeof f.id === "number" &&
    typeof f.event === "string" &&
    typeof f.title === "string" &&
    typeof f.capture === "string" &&
    typeof f.interpret === "string" &&
    typeof f.method === "string" &&
    typeof f.wow === "string" &&
    typeof f.chain_to === "number"
  );
}

/**
 * Fetches /gaze_factors.json once and caches the parsed factors. Safe to call
 * repeatedly (returns the same in-flight promise, then the cached array). On any
 * failure it resolves to an empty array and never throws, so a UI can rely on it.
 */
export async function loadGazeChain(): Promise<GazeFactor[]> {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const res = await fetch("/gaze_factors.json", { cache: "force-cache" });
      if (!res.ok) return [];
      const data = (await res.json()) as GazeChainFile;
      if (Array.isArray(data.factors)) {
        FACTORS = data.factors.filter(isFactor);
      }
      return FACTORS;
    } catch {
      // Swallow: this is an additive layer and must never break the app.
      return [];
    }
  })();
  return loadPromise;
}

/**
 * Walks the chain by one step: returns the factor that `fromId` links to, or
 * null if `fromId` is unknown or the target is missing. Returns null if the
 * chain has not been loaded yet (call loadGazeChain first).
 */
export function nextFactor(fromId: number): GazeFactor | null {
  const current = FACTORS.find((f) => f.id === fromId);
  if (!current) return null;
  return FACTORS.find((f) => f.id === current.chain_to) ?? null;
}

/**
 * Returns the factor for a given gaze event name (exact match, e.g.
 * "fixation-detection"), or null if none is loaded or found.
 */
export function factorForEvent(event: string): GazeFactor | null {
  return FACTORS.find((f) => f.event === event) ?? null;
}
